<script setup lang="ts">
import { computed } from 'vue'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
	ShieldCheckIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	XCircleIcon,
	QuestionMarkCircleIcon,
	GlobeAltIcon,
	CameraIcon,
	FingerPrintIcon,
} from '@heroicons/vue/24/solid'

interface FakeDetection {
	editor_tag?: string
	signals?: Record<string, any>
	reasons?: string[]
	methods?: Record<string, any>
	probability?: number
}

interface WebDetect {
	similar_image_match?: string | any[]
	matching_image?: string | any[]
	web_page_match?: string | any[]
}

const props = defineProps<{
	systemMeta?: Record<string, any>
}>()

// Parse fake_detection from system metadata
const fakeDetection = computed<FakeDetection | null>(() => {
	if (!props.systemMeta) return null
	const fd = props.systemMeta['fake_detection']
	if (!fd) return null
	if (typeof fd === 'string') {
		try { return JSON.parse(fd) } catch { return null }
	}
	return fd
})

// Parse webdetect from system metadata
const webDetect = computed<WebDetect | null>(() => {
	if (!props.systemMeta) return null
	const wd = props.systemMeta['webdetect']
	if (!wd) return null
	if (typeof wd === 'string') {
		try { return JSON.parse(wd) } catch { return null }
	}
	return wd
})

// Extract selfhosted_ml from fake_detection.methods
const selfhostedML = computed(() => {
	const methods = fakeDetection.value?.methods
	if (!methods?.selfhosted_ml) return null
	return methods.selfhosted_ml
})

// Extract c2pa from fake_detection.methods
const c2pa = computed(() => {
	const methods = fakeDetection.value?.methods
	if (!methods?.c2pa) return null
	return methods.c2pa
})

// ML model results parsed into display format
const mlModels = computed(() => {
	const details = selfhostedML.value?.details
	if (!details?.model_results) return []
	return Object.entries(details.model_results).map(([name, data]: [string, any]) => ({
		name: name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
		probability: data.probability ?? null,
		label: data.prediction ?? data.class ?? null,
	}))
})

const ensembleScore = computed(() => {
	return selfhostedML.value?.probability ?? null
})
const ensembleMethod = computed(() => {
	return selfhostedML.value?.details?.ml_ensemble_method ?? null
})

const hasData = computed(() => fakeDetection.value || webDetect.value)

// Verdict
const verdict = computed(() => {
	const tag = fakeDetection.value?.editor_tag
	if (!tag) return { label: 'Unknown', color: 'bg-muted text-muted-foreground', icon: QuestionMarkCircleIcon }
	if (tag === 'likely_real') return { label: 'Likely Authentic', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', icon: ShieldCheckIcon }
	if (tag === 'likely_fake' || tag === 'fake') return { label: 'Likely Manipulated', color: 'bg-red-500/15 text-red-700 dark:text-red-400', icon: ExclamationTriangleIcon }
	if (tag === 'synthetic_likely' || tag === 'synthetic') return { label: 'Synthetic Likely', color: 'bg-red-500/15 text-red-700 dark:text-red-400', icon: ExclamationTriangleIcon }
	if (tag === 'uncertain' || tag === 'inconclusive') return { label: 'Inconclusive', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', icon: QuestionMarkCircleIcon }
	const prettyLabel = tag.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
	return { label: prettyLabel, color: 'bg-muted text-muted-foreground', icon: QuestionMarkCircleIcon }
})

// Signal definitions for display
const signalDefs: Record<string, { label: string; description: string; positive: string; negative: string }> = {
	camera_confident: { label: 'Camera Confidence', description: 'Camera metadata verified', positive: 'Camera signature verified', negative: 'Camera signature not found' },
	metadata_stripped: { label: 'Metadata Stripped', description: 'Original metadata present', positive: 'Metadata has been stripped', negative: 'Original metadata intact' },
	gen_dimensions: { label: 'AI Dimensions', description: 'Image dimensions match AI generator patterns', positive: 'Dimensions match AI patterns', negative: 'Normal dimensions' },
	web_exact_match: { label: 'Web Exact Match', description: 'Exact copy found on the web', positive: 'Exact match found online', negative: 'No exact match found' },
	web_ai_labeled: { label: 'Web AI Labeled', description: 'Image found labeled as AI-generated online', positive: 'Found labeled as AI online', negative: 'Not labeled as AI online' },
	vote_split: { label: 'Community Vote Split', description: 'Community opinion is divided', positive: 'Votes are split', negative: 'Community consensus' },
	fake_votes: { label: 'Fake Votes', description: 'Community votes for fake', positive: 'Has fake votes', negative: 'No fake votes' },
	real_votes: { label: 'Real Votes', description: 'Community votes for real', positive: 'Has real votes', negative: 'No real votes' },
	strong_model_fake: { label: 'AI Model Detection', description: 'AI model detects manipulation', positive: 'AI model flags as fake', negative: 'AI model says authentic' },
	strong_model_name: { label: 'Detection Model', description: 'Which AI model was used', positive: '', negative: '' },
	strong_model_prob: { label: 'Model Confidence', description: 'Detection model confidence level', positive: '', negative: '' },
	// ML ensemble signals
	clip_probability: { label: 'CLIP Universal', description: 'CLIP-based universal detector probability', positive: 'Low fake probability', negative: 'High fake probability' },
	cnn_probability: { label: 'CNN ResNet50', description: 'CNN ResNet50 detector probability', positive: 'Low fake probability', negative: 'High fake probability' },
	freq_probability: { label: 'Frequency Analysis', description: 'Frequency-domain EfficientNet probability', positive: 'Low fake probability', negative: 'High fake probability' },
	ensemble_score: { label: 'Ensemble Score', description: 'Combined score from all ML models', positive: '', negative: '' },
	ensemble_method: { label: 'Ensemble Method', description: 'How model scores were combined', positive: '', negative: '' },
	// C2PA signals
	c2pa_signed: { label: 'C2PA Signed', description: 'Content has C2PA content credentials', positive: 'Content is signed', negative: 'No C2PA signature' },
	c2pa_ai_generated: { label: 'C2PA AI Generated', description: 'C2PA metadata indicates AI generation', positive: 'Marked as AI-generated', negative: 'Not marked as AI' },
}

// Parsed signals
const signals = computed(() => {
	const raw = fakeDetection.value?.signals
	if (!raw) return []
	return Object.entries(raw).map(([key, value]) => {
		const def = signalDefs[key] || { label: key.replace(/_/g, ' '), description: '', positive: '', negative: '' }
		const isBoolean = typeof value === 'boolean'
		const isNumeric = typeof value === 'number'
		const isNull = value === null || value === undefined
		// For boolean signals, "good" depends on the signal
		const invertedSignals = ['metadata_stripped', 'gen_dimensions', 'web_exact_match', 'web_ai_labeled', 'vote_split', 'fake_votes', 'strong_model_fake']
		const isGood = isBoolean ? (invertedSignals.includes(key) ? !value : !!value) : null
		return { key, value, def, isBoolean, isNumeric, isNull, isGood }
	})
})

// Reasons
const reasons = computed(() => fakeDetection.value?.reasons || [])

// Similar images
const similarImages = computed(() => {
	const raw = webDetect.value?.similar_image_match
	if (!raw) return []
	if (typeof raw === 'string') {
		try { return JSON.parse(raw) } catch { return [] }
	}
	return Array.isArray(raw) ? raw : []
})

// Web page matches
const webPageMatches = computed(() => {
	const raw = webDetect.value?.web_page_match
	if (!raw) return []
	if (typeof raw === 'string') {
		try { return JSON.parse(raw) } catch { return [] }
	}
	return Array.isArray(raw) ? raw : []
})

// Exact matches
const exactMatches = computed(() => {
	const raw = webDetect.value?.matching_image
	if (!raw) return []
	if (typeof raw === 'string') {
		try { return JSON.parse(raw) } catch { return [] }
	}
	return Array.isArray(raw) ? raw : []
})

// Count of passing signals
const passingSignals = computed(() => signals.value.filter(s => s.isGood === true).length)
const totalBooleanSignals = computed(() => signals.value.filter(s => s.isBoolean).length)
</script>

<template>
	<div v-if="hasData" class="space-y-3">
		<!-- Verdict Banner -->
		<div :class="[verdict.color, 'rounded-lg p-3 flex items-center gap-3']">
			<component :is="verdict.icon" class="h-8 w-8 flex-shrink-0" />
			<div>
				<div class="font-semibold text-sm">{{ verdict.label }}</div>
				<div class="text-xs opacity-80" v-if="reasons.length">
					Based on: {{ reasons.map(r => r.replace(/_/g, ' ')).join(', ') }}
				</div>
				<div class="text-xs opacity-80" v-if="totalBooleanSignals > 0">
					{{ passingSignals }}/{{ totalBooleanSignals }} signals passed
				</div>
			</div>
		</div>

		<!-- Signal Grid -->
		<div v-if="signals.length > 0">
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Detection Signals</h3>
			<div class="grid grid-cols-1 gap-1">
				<TooltipProvider>
					<div
						v-for="signal in signals"
						:key="signal.key"
						class="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50 text-xs"
					>
						<div class="flex items-center gap-2">
							<Tooltip>
								<TooltipTrigger as-child>
									<div class="flex items-center gap-2">
										<!-- Status icon -->
										<CheckCircleIcon v-if="signal.isGood === true" class="h-4 w-4 text-emerald-500 flex-shrink-0" />
										<XCircleIcon v-else-if="signal.isGood === false" class="h-4 w-4 text-red-500 flex-shrink-0" />
										<QuestionMarkCircleIcon v-else class="h-4 w-4 text-muted-foreground flex-shrink-0" />
										<span>{{ signal.def.label }}</span>
									</div>
								</TooltipTrigger>
								<TooltipContent side="left">
									<p class="text-xs max-w-48">{{ signal.def.description }}</p>
								</TooltipContent>
							</Tooltip>
						</div>
						<div>
							<Badge v-if="signal.isBoolean" :variant="signal.isGood ? 'default' : 'destructive'" class="text-[10px] h-5">
								{{ signal.isGood ? 'Pass' : 'Flag' }}
							</Badge>
							<Badge v-else-if="signal.isNull" variant="outline" class="text-[10px] h-5">N/A</Badge>
							<Badge v-else variant="secondary" class="text-[10px] h-5">{{ signal.value }}</Badge>
						</div>
					</div>
				</TooltipProvider>
			</div>
		</div>

		<!-- ML Ensemble Results (from fake_detection.methods.selfhosted_ml) -->
		<div v-if="selfhostedML">
			<Separator class="my-3" />
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
				<CameraIcon class="h-3.5 w-3.5" /> ML Ensemble Analysis
			</h3>

			<!-- Ensemble Score Bar -->
			<div v-if="ensembleScore !== null" class="mb-3">
				<div class="flex items-center justify-between text-xs mb-1">
					<span class="text-muted-foreground">Ensemble Score</span>
					<span class="font-medium" :class="{
						'text-emerald-600 dark:text-emerald-400': ensembleScore < 0.3,
						'text-amber-600 dark:text-amber-400': ensembleScore >= 0.3 && ensembleScore < 0.7,
						'text-red-600 dark:text-red-400': ensembleScore >= 0.7
					}">{{ (ensembleScore * 100).toFixed(1) }}%</span>
				</div>
				<div class="w-full h-2 bg-muted rounded-full overflow-hidden">
					<div class="h-full rounded-full transition-all" :class="{
						'bg-emerald-500': ensembleScore < 0.3,
						'bg-amber-500': ensembleScore >= 0.3 && ensembleScore < 0.7,
						'bg-red-500': ensembleScore >= 0.7
					}" :style="{ width: (ensembleScore * 100) + '%' }"></div>
				</div>
				<div v-if="ensembleMethod" class="text-[10px] text-muted-foreground mt-1">
					Method: {{ ensembleMethod.replace(/_/g, ' ') }}
				</div>
			</div>

			<!-- Per-Model Probabilities -->
			<div v-if="mlModels.length > 0" class="space-y-2">
				<div v-for="model in mlModels" :key="model.name" class="text-xs">
					<div class="flex items-center justify-between mb-0.5">
						<span>{{ model.name }}</span>
						<div class="flex items-center gap-2">
							<Badge v-if="model.label" variant="outline" class="text-[10px] h-5">{{ model.label }}</Badge>
							<span v-if="model.probability !== null" class="font-mono text-[10px]" :class="{
								'text-emerald-600 dark:text-emerald-400': model.probability < 0.3,
								'text-amber-600 dark:text-amber-400': model.probability >= 0.3 && model.probability < 0.7,
								'text-red-600 dark:text-red-400': model.probability >= 0.7
							}">{{ (model.probability * 100).toFixed(1) }}%</span>
						</div>
					</div>
					<div v-if="model.probability !== null" class="w-full h-1.5 bg-muted rounded-full overflow-hidden">
						<div class="h-full rounded-full transition-all" :class="{
							'bg-emerald-500': model.probability < 0.3,
							'bg-amber-500': model.probability >= 0.3 && model.probability < 0.7,
							'bg-red-500': model.probability >= 0.7
						}" :style="{ width: (model.probability * 100) + '%' }"></div>
					</div>
				</div>
			</div>

			<!-- ML Reasons -->
			<div v-if="selfhostedML.reasons && selfhostedML.reasons.length > 0" class="mt-2">
				<div class="flex flex-wrap gap-1">
					<Badge v-for="(reason, i) in selfhostedML.reasons" :key="i" variant="outline" class="text-[10px]">{{ reason.replace(/_/g, ' ') }}</Badge>
				</div>
			</div>
		</div>

		<!-- C2PA Content Credentials (from fake_detection.methods.c2pa) -->
		<div v-if="c2pa">
			<Separator class="my-3" />
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
				<ShieldCheckIcon class="h-3.5 w-3.5" /> C2PA Content Credentials
			</h3>
			<div class="space-y-1.5">
				<!-- C2PA Present -->
				<div class="flex items-center justify-between py-1 px-2 rounded text-xs hover:bg-muted/50">
					<div class="flex items-center gap-2">
						<CheckCircleIcon v-if="c2pa.details?.c2pa_present" class="h-4 w-4 text-emerald-500 flex-shrink-0" />
						<XCircleIcon v-else class="h-4 w-4 text-muted-foreground flex-shrink-0" />
						<span>C2PA Manifest</span>
					</div>
					<Badge :variant="c2pa.details?.c2pa_present ? 'default' : 'outline'" class="text-[10px] h-5">
						{{ c2pa.details?.c2pa_present ? 'Found' : 'Not Found' }}
					</Badge>
				</div>
				<!-- Signature Valid -->
				<div v-if="c2pa.details?.signature_valid !== undefined" class="flex items-center justify-between py-1 px-2 rounded text-xs hover:bg-muted/50">
					<div class="flex items-center gap-2">
						<CheckCircleIcon v-if="c2pa.details.signature_valid" class="h-4 w-4 text-emerald-500 flex-shrink-0" />
						<XCircleIcon v-else class="h-4 w-4 text-red-500 flex-shrink-0" />
						<span>Signature Valid</span>
					</div>
					<Badge :variant="c2pa.details.signature_valid ? 'default' : 'destructive'" class="text-[10px] h-5">
						{{ c2pa.details.signature_valid ? 'Yes' : 'No' }}
					</Badge>
				</div>
				<!-- Classification -->
				<div v-if="c2pa.class" class="flex items-center justify-between py-1 px-2 rounded text-xs hover:bg-muted/50">
					<div class="flex items-center gap-2">
						<ExclamationTriangleIcon v-if="c2pa.class === 'fake'" class="h-4 w-4 text-amber-500 flex-shrink-0" />
						<CheckCircleIcon v-else class="h-4 w-4 text-emerald-500 flex-shrink-0" />
						<span>C2PA Classification</span>
					</div>
					<Badge :variant="c2pa.class === 'fake' ? 'destructive' : 'default'" class="text-[10px] h-5">
						{{ c2pa.class === 'fake' ? 'AI Generated' : 'Authentic' }}
					</Badge>
				</div>
				<!-- Claim Generator -->
				<div v-if="c2pa.details?.claim_generator" class="flex items-center justify-between py-1 px-2 rounded text-xs hover:bg-muted/50">
					<span class="text-muted-foreground">Claim Generator</span>
					<span class="font-medium truncate ml-2 max-w-[160px]">{{ c2pa.details.claim_generator }}</span>
				</div>
				<!-- Reasons -->
				<div v-if="c2pa.reasons && c2pa.reasons.length > 0" class="py-1 px-2 text-xs">
					<span class="text-muted-foreground">Signals:</span>
					<div class="flex flex-wrap gap-1 mt-1">
						<Badge v-for="(reason, i) in c2pa.reasons" :key="i" variant="outline" class="text-[10px]">{{ reason.replace(/_/g, ' ') }}</Badge>
					</div>
				</div>
			</div>
		</div>

		<Separator v-if="similarImages.length > 0 || exactMatches.length > 0 || webPageMatches.length > 0" />

		<!-- Web Detection Results -->
		<div v-if="exactMatches.length > 0">
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
				<FingerPrintIcon class="h-3.5 w-3.5" /> Exact Matches ({{ exactMatches.length }})
			</h3>
			<div class="grid grid-cols-3 gap-1.5">
				<a
					v-for="(match, i) in exactMatches"
					:key="i"
					:href="match.link || match.url || match"
					target="_blank"
					class="block rounded border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
				>
					<img :src="match.image || match.thumbnail || match" class="w-full h-16 object-cover" loading="lazy" />
				</a>
			</div>
		</div>

		<div v-if="similarImages.length > 0">
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
				<GlobeAltIcon class="h-3.5 w-3.5" /> Similar Images Found ({{ similarImages.length }})
			</h3>
			<div class="grid grid-cols-3 gap-1.5">
				<a
					v-for="(match, i) in similarImages"
					:key="i"
					:href="match.link || match.url || '#'"
					target="_blank"
					class="block rounded border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
				>
					<img :src="match.image || match.thumbnail || match" class="w-full h-16 object-cover" loading="lazy" />
				</a>
			</div>
		</div>

		<div v-if="webPageMatches.length > 0">
			<h3 class="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
				<GlobeAltIcon class="h-3.5 w-3.5" /> Web Page Matches ({{ webPageMatches.length }})
			</h3>
			<div class="space-y-1">
				<a
					v-for="(match, i) in webPageMatches"
					:key="i"
					:href="match.link || match.url || match"
					target="_blank"
					class="block text-xs text-primary hover:underline truncate"
				>
					{{ match.title || match.link || match.url || match }}
				</a>
			</div>
		</div>
	</div>

	<!-- No Data State -->
	<div v-else class="flex flex-col items-center justify-center py-8 text-muted-foreground">
		<ShieldCheckIcon class="h-8 w-8 mb-2 opacity-40" />
		<p class="text-xs">No AI scan results available</p>
		<p class="text-[10px] opacity-60 mt-1">DeepSafe scanning data will appear here once processed</p>
	</div>
</template>
